"use client";
import Head from "next/head";
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { getDefaultLanguage, getSelectedBrandData, getSelectedBrandEnabledLanguages } from "~/cores/appCore";
import PageInformationCard from "~/components/PageInformationCard.jsx";
import { TurnrGrid } from "~/components/TurnrGrid.jsx";
import TurnrDropdownSelect from "~/components/input/TurnrDropdownSelect.jsx";
import { TurnrCard, TurnrCardContent, TurnrCardHeader } from "~/components/card/TurnrCard.jsx";
import { TurnrActionAlert } from "~/components/alerts/TurnrAlert.jsx";
import LanguageSelect from "~/components/input/selects/LanguageSelect.jsx";
import BrandNeededWrapper from "~/components/BrandNeededWrapper.jsx";
import translate from "translate";
import { TurnrButton } from "~/components/buttons/TurnrButton";
import { faWandMagicSparkles } from "@fortawesome/pro-regular-svg-icons";
import { brandButtonTypeToPx } from "~/cores/brandCore";
import LanguageFlag from "~/components/LanguageFlag.jsx";
import { TurnrTooltip } from "rylak-common/src/components/TurnrTooltip/TurnrTooltip";
import { LanguageDropdownRender } from "~/components/input/selects/MultipleLanguageSelect.jsx";
import { fetchEmailTemplates, updateEmailTemplate } from "~/services";
import * as styles from "./index.module.scss";
import classnames from "classnames";
import { CHIP_COLOR_TYPES, CustomChip } from "~/components/chips/CustomChip.jsx";
import { COLORS } from "rylak-common/src/constants";
import { TurnrServiceSnackbar } from "~/components/snackbars/TurnrServiceSnackbars.jsx";
import SkeletonLoaderCardSlim from "~/components/loading/skeletons/SkeletonLoaderCardSlim.jsx";
import _ from "lodash";
import { ServiceErrorAlert } from "~/components/Error.jsx";
import TurnrInput from "~/components/input/TurnrInput.jsx";
import EmailEditor from "react-email-editor";
import { serviceIsLoading, serviceIsSuccessfullyFetched, useServiceHook } from "rylak-common";
import { getDesignSettingsPageData, getEmailTemplatePageData } from "~/cores/pageAccessCore";
import TurnrLink from "~/components/links/TurnrLink";
import { useAppProps } from "~/app/_apps/AppPropsContext";

translate.engine = "google"; // "google", "yandex", "libre", "deepl"

function VariableChips({ variables }) {
    return (
        <TurnrGrid container item xs={12} spacing={1} alignItems={"center"}>
            <TurnrGrid item>
                <div className={"muted subtitle"} style={{ fontSize: "12px" }}>
                    Available variables:
                </div>
            </TurnrGrid>
            {variables.map((v) => (
                <TurnrGrid item key={v.value}>
                    <CustomChip
                        label={v.value}
                        tooltipTitle={
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(2, auto)",
                                    gridRowGap: "10px",
                                    gridColumnGap: "20px",
                                }}
                            >
                                <div className={"muted"}>Name:</div>
                                <div>{v.name}</div>
                                <div className={"muted"}>Written:</div>
                                <div>{`{{${v.value}}}`}</div>
                                {v.description && <div className={"muted"}>Description: </div>}
                                {v.description && <div>{v.description}</div>}
                                {v.sample && <div className={"muted"}>Sample:</div>}
                                {v.sample && <div>{v.sample}</div>}
                                <div>Required:</div>
                                <div
                                    style={{
                                        color: v.required && COLORS.ACTION,
                                    }}
                                >
                                    <b>{v.required ? "Yes" : "No"}</b>
                                </div>
                            </div>
                        }
                        colorType={v.required ? CHIP_COLOR_TYPES.ACTION : CHIP_COLOR_TYPES.DONE}
                    />
                </TurnrGrid>
            ))}
        </TurnrGrid>
    );
}

function getObjectsWithKey(theObject, key) {
    var result = [];
    if (theObject instanceof Array) {
        for (var i = 0; i < theObject.length; i++) {
            result = result.concat(getObjectsWithKey(theObject[i], key));
        }
    } else {
        for (var prop in theObject) {
            if (prop === key) {
                if (theObject[prop]) {
                    return theObject;
                }
            }
            if (theObject[prop] instanceof Object || theObject[prop] instanceof Array) {
                result = result.concat(getObjectsWithKey(theObject[prop], key));
            }
        }
    }
    return result;
}

function getTemplateId(type, languageCode) {
    if (!type || !languageCode) {
        return null;
    }
    return type + "_" + languageCode;
}

function templateToId(template) {
    return getTemplateId(template.type, template.languageCode);
}

export default function SettingsEmailTemplatePage({ ref }) {
    const { appProps } = useAppProps();

    const emailEditorRef = useRef(null);
    const hiddenEmailEditorRef = useRef(null);

    const [saving, setSaving] = useState(false);
    const [loadingTemplateType, setLoadingTemplateType] = useState(null);
    const [selectedTemplateType, setSelectedTemplateType] = useState(null);
    const [firstTemplateIsReady, setFirstTemplateIsReady] = useState(false);
    const [saveChangesDone, setSaveChangesDone] = useState(true);
    const [loadingBlank, setLoadingBlank] = useState(false);

    const [selectedLanguage, setSelectedLanguage] = useState(getDefaultLanguage(appProps));
    const [cloneFromTemplateType, setCloneFromTemplateType] = useState(null);
    const [templateChanges, setTemplateChanges] = useState({});
    const [confirmCallbackFn, setConfirmCallbackFn] = useState(null);
    const [subject, setSubject] = useState("");
    const [originalSubject, setOriginalSubject] = useState("");
    const [preHeader, setPreHeader] = useState("");
    const [originalPreHeader, setOriginalPreHeader] = useState("");
    const [resetKey, setResetKey] = useState(false);
    const [blankDesign, setBlankDesign] = useState(null);

    const { serviceData: emailTemplatesData } = useServiceHook({
        appProps,
        fetchRequested: true,
        fetchFn: fetchEmailTemplates,
    });

    const { serviceData: updateEmailTemplateServiceData, fetchServiceData: updateEmailTemplateFn } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchFn: updateEmailTemplate,
    });

    const emailTemplateGroups = useMemo(() => emailTemplatesData?.data?.emailTemplateGroups, [emailTemplatesData, resetKey]);
    const allEmailTemplatesMetaData = useMemo(() => _.flatMap(emailTemplateGroups, (templateGroup) => templateGroup?.templates), [emailTemplateGroups, resetKey]);

    const allEmailTemplatesAsMap = useMemo(
        () =>
            allEmailTemplatesMetaData.reduce((r, metaData) => {
                Object.values(metaData?.languages)?.forEach((l) => {
                    r[templateToId(l)] = l;
                });
                return r;
            }, {}),
        [allEmailTemplatesMetaData],
    );

    useEffect(() => {
        if (serviceIsSuccessfullyFetched(updateEmailTemplateServiceData)) {
            // let foundTemplate = null;
            // let foundTemplateMetaData = null;
            const newTemplate = updateEmailTemplateServiceData?.data;
            const newTemplateType = newTemplate?.type;
            const newLanguageCode = newTemplate?.languageCode;
            emailTemplatesData?.data?.emailTemplateGroups?.find((emailTemplateGroup) =>
                emailTemplateGroup?.templates?.find((t) => {
                    if (t.type === newTemplateType) {
                        t.languages[newLanguageCode] = newTemplate;
                        // foundTemplateMetaData = t;
                        return t;
                    }
                }),
            );
            delete templateChanges[templateToId(newTemplate)];
            setResetKey(!resetKey);
        }
    }, [updateEmailTemplateServiceData]);

    useImperativeHandle(
        ref,
        () => ({
            prepareNavigation(callbackFn) {
                if (!firstTemplateIsReady) {
                    callbackFn();
                } else {
                    setSaveChangesDone(false);
                    setConfirmCallbackFn({ fn: callbackFn });
                    saveChangesLocally();
                }
            },
            confirmNavigation() {
                return Object.values(allEmailTemplatesAsMap).find((template) => hasTemplateChanges(templateToId(template)));
            },
        }),
        [allEmailTemplatesAsMap, templateChanges, firstTemplateIsReady],
    );

    useEffect(() => {
        if (confirmCallbackFn && saveChangesDone) {
            confirmCallbackFn?.fn();
            setConfirmCallbackFn(null);
            setSaveChangesDone(false);
        }
    }, [templateChanges, confirmCallbackFn, saveChangesDone]);

    const originalTemplateDesigns = useMemo(
        () =>
            allEmailTemplatesMetaData.reduce((r, metaData) => {
                Object.values(metaData?.languages)?.forEach((l) => {
                    r[templateToId(l)] = { design: JSON.parse(l.templateJson) };
                });
                return r;
            }, {}),
        [allEmailTemplatesMetaData],
    );

    const selectedTemplateMetaData = useMemo(
        () =>
            allEmailTemplatesMetaData?.find((template) => {
                return template?.type === selectedTemplateType;
            }),
        [allEmailTemplatesMetaData, selectedTemplateType],
    );
    const selectedTemplate = useMemo(() => selectedTemplateMetaData?.languages?.[selectedLanguage], [selectedTemplateMetaData, selectedLanguage]);
    const selectedTemplateId = useMemo(() => getTemplateId(selectedTemplateType, selectedLanguage), [selectedTemplateType, selectedLanguage]);

    function getPreHeaderFromTemplateDesignId(templateId) {
        if (templateChanges[templateId]?.preHeader || templateChanges[templateId]?.preHeader === "") {
            return templateChanges[templateId]?.preHeader;
        }
        const design = getLocalTemplateDesign(templateId);
        return getPreHeaderFromTemplateDesign(design);
    }

    function getPreHeaderFromTemplateDesign(design) {
        return design?.body?.values?.preheaderText || "";
    }

    useEffect(() => {
        if (selectedTemplateId) {
            loadTemplate(selectedTemplateId);
        }
    }, [selectedTemplateId]);

    useEffect(() => {
        if (selectedTemplateId) {
            setSubject(templateChanges[selectedTemplateId] ? templateChanges[selectedTemplateId]?.subject : selectedTemplate?.subject || "");
            setOriginalSubject(selectedTemplate?.subject || "");
            setPreHeader(templateChanges[selectedTemplateId] ? templateChanges[selectedTemplateId]?.preHeader : getPreHeaderFromTemplateDesignId(selectedTemplateId));
            setOriginalPreHeader(getPreHeaderFromTemplateDesign(originalTemplateDesigns[selectedTemplateId]?.design));
        }
    }, [selectedTemplateId]);

    const mergeTags = useMemo(
        () =>
            selectedTemplateMetaData?.variables?.reduce((mt, variable) => {
                mt[variable.value] = {
                    name: variable.name,
                    value: `{{${variable.value}}}`,
                    sample: variable.sample,
                    required: variable.required,
                };
                return mt;
            }, {}),
        [selectedTemplateMetaData],
    );

    const requiredVariables = selectedTemplateMetaData?.variables?.filter((v) => v.required);

    const optionalVariables = selectedTemplateMetaData?.variables?.filter((v) => !v.required);

    function saveChangesLocally({ design, onSaveChangesDone } = {}) {
        if (firstTemplateIsReady) {
            setSaveChangesDone(false);
            if (design) {
                templateChanges[selectedTemplateId] = {
                    design: JSON.parse(JSON.stringify(design)),
                    subject,
                    preHeader,
                };
                setTemplateChanges({ ...templateChanges });
                setSaveChangesDone(true);
                onSaveChangesDone?.();
            } else {
                const localChangesId = selectedTemplateId;
                emailEditorRef.current?.editor?.exportHtml((data) => {
                    const { design } = data;
                    templateChanges[localChangesId] = {
                        design: JSON.parse(JSON.stringify(design)),
                        subject,
                        preHeader,
                    };

                    setTemplateChanges({ ...templateChanges });
                    setTimeout(() => {
                        setSaveChangesDone(true);
                        onSaveChangesDone?.();
                    }, 500);
                });
            }
        } else {
            onSaveChangesDone();
        }
    }

    function hasTemplateChanges(templateId) {
        const templateChangesDesign = templateChanges[templateId]?.design && _.omit(templateChanges[templateId]?.design, "schemaVersion");
        const originalDesign = originalTemplateDesigns[templateId]?.design && _.omit(originalTemplateDesigns[templateId]?.design, "schemaVersion");

        const hasChanges =
            templateChangesDesign &&
            // This is very buggy atm so we remove this check
            // (originalTemplateDesigns[templateId] ? !_.isEqual(templateChangesDesign, originalDesign) :
            //         !_.isEqual(templateChangesDesign, blankDesign)
            // )
            // ||
            ((templateChanges[templateId]?.subject || "") !== (allEmailTemplatesAsMap[templateId]?.subject || "") ||
                getPreHeaderFromTemplateDesign(templateChangesDesign) !== getPreHeaderFromTemplateDesign(originalDesign));

        return hasChanges;
    }

    function resetLocalChanges() {
        setSubject(originalSubject);
        setPreHeader(originalPreHeader);
        delete templateChanges[selectedTemplateId];
        setTemplateChanges({ ...templateChanges });
        loadTemplate();
    }

    const onSaveClick = () => {
        setSaving(true);
        emailEditorRef.current.editor.exportHtml((data) => {
            const { design } = data;
            design.body.values.preheaderText = preHeader;
            hiddenEmailEditorRef?.current?.editor?.loadDesign(design);
            saveChangesLocally({
                design,
                onSaveChangesDone: () => {
                    hiddenEmailEditorRef.current?.editor?.exportHtml((data) => {
                        const { design, html } = data;
                        // const formattedHtml = html?.replace(/(\r\n|\n|\r)/gm, "").replaceAll("\"", "\\\"");
                        updateEmailTemplateFn({
                            type: selectedTemplateType,
                            languageCode: selectedLanguage,
                            templateJson: JSON.stringify(design),
                            templateHtml: html,
                            subject,
                        });
                        setSaving(false);
                    });
                },
            });
        });
    };

    const editorToolsSettings = {
        button: {
            properties: {
                buttonColors: {
                    value: {
                        color: "#FFFFFF",
                        backgroundColor: "#000000",
                        hoverColor: "#ffffff",
                        hoverBackgroundColor: "#1e1e1e",
                    },
                },
                borderRadius: {
                    value: brandButtonTypeToPx(getSelectedBrandData(appProps)),
                },
            },
        },
        text: {
            properties: {
                color: {
                    value: "#000000",
                },
            },
        },
        menu: {
            enabled: false,
        },
    };

    const editorFeatureSettings = {
        features: { preheaderText: true },
        tools: editorToolsSettings,
    };

    function translateClick() {
        emailEditorRef.current.editor.saveDesign((design) => {
            // let designAsString = JSON.stringify(design);
            const objectsWithText = getObjectsWithKey(design, "text");
            let counter = 0;
            objectsWithText.forEach((object) => {
                translate(object.text, selectedLanguage).then((translatedText) => {
                    object.text = translatedText;
                    counter += 1;
                    if (counter === objectsWithText.length) {
                        emailEditorRef.current.editor.loadDesign(design);
                        saveChangesLocally({ design });
                    }
                });
            });
        });
    }

    const dropdownOptions = allEmailTemplatesMetaData.map((t) => ({
        id: t.type,
        label: t.name,
    }));

    function cloneFromTemplateId(templateType, languageCode) {
        const templateId = getTemplateId(templateType, languageCode); //allEmailTemplatesMetaData.find(m => m.type === templateType)?.languages[languageCode]?.id;
        const templateDesignToClone = getLocalTemplateDesign(templateId);
        saveChangesLocally({ design: templateDesignToClone });
        emailEditorRef?.current?.editor?.loadDesign(getLocalTemplateDesign(templateId));
    }

    function getLocalTemplateDesign(templateId) {
        return templateChanges[templateId]?.design || originalTemplateDesigns[templateId]?.design;
    }

    function loadTemplate(templateId = selectedTemplateId) {
        if (templateId) {
            // setLoadingTemplateType(templateId);
            const design = getLocalTemplateDesign(templateId);
            if (design) {
                emailEditorRef?.current?.editor?.loadDesign(getLocalTemplateDesign(templateId));
            } else if (blankDesign) {
                emailEditorRef?.current?.editor?.loadDesign(blankDesign);
            } else {
                emailEditorRef?.current?.editor?.loadBlank();
            }
        }
    }

    const onHiddenLoad = useCallback(() => {}, []);
    useEffect(() => {
        if (hiddenEmailEditorRef?.current) {
            hiddenEmailEditorRef?.current?.editor?.loadBlank();
        }
    }, [hiddenEmailEditorRef?.current]);

    const onHiddenReady = useCallback(() => {
        if (!blankDesign && !loadingBlank) {
            setLoadingBlank(true);
            hiddenEmailEditorRef.current.editor.exportHtml((data) => {
                const { design } = data;
                setBlankDesign(JSON.parse(JSON.stringify(design)));
                setLoadingBlank(false);
            });
        }
    }, [blankDesign, loadingBlank]);

    const onReady = useCallback(() => {
        // editor is ready
        // you can load your template here;
        if (selectedTemplateId === loadingTemplateType) {
            emailEditorRef?.current?.editor?.setMergeTags(mergeTags);
            setTimeout(() => {
                setLoadingTemplateType(null);
            }, 500);
        }
        setFirstTemplateIsReady(true);
    }, [selectedTemplateId, loadingTemplateType, mergeTags]);

    useEffect(() => {
        if (firstTemplateIsReady && blankDesign) {
            loadTemplate();
        }
    }, [firstTemplateIsReady, blankDesign]);

    const updateIsLoading = serviceIsLoading(updateEmailTemplateServiceData) || saving;

    const anythingIsLoading = (selectedTemplateType && !firstTemplateIsReady) || !saveChangesDone || updateIsLoading || saving || loadingBlank;

    function onTemplateMenuItemClick(template) {
        saveChangesLocally({
            onSaveChangesDone: () => {
                setSelectedTemplateType(template?.type);
            },
        });
    }

    return (
        <>
            <Head>
                <title>Email Templates</title>
            </Head>
            <TurnrServiceSnackbar appProps={appProps} successTitle="Successfully saved email template!" serviceData={updateEmailTemplateServiceData} />
            <PageInformationCard
                iconProps={{ flip: "horizontal" }}
                icon={getEmailTemplatePageData().icon}
                title={"Email Templates"}
                content={
                    <div>
                        Here you can view and edit some of the content of all the different emails that get send out to your customers.
                        <br />
                        <br />
                        And don&apos;t worry. We have default content for each email so you can decide if you want to make some changes or just let them be as they are.
                        <br />
                        <br />
                        The language that will be used for the emails is the language the customer selected when making the Return. If a template of a certain language is missing then the system will
                        default to sending the english version.
                        <br />
                        <br />
                        If you want to enable more languages then you need to activate them in <TurnrLink href={getDesignSettingsPageData().href}>App Design</TurnrLink>
                    </div>
                }
            />
            <BrandNeededWrapper appProps={appProps}>
                <TurnrGrid container spacing={2} wrap={"nowrap"}>
                    <TurnrGrid item xs={"auto"}>
                        <ServiceErrorAlert serviceData={emailTemplatesData} noMargins />
                        <TurnrCard
                            className={styles["side-menu"]}
                            SkeletonLoaderCard={SkeletonLoaderCardSlim}
                            loading={serviceIsLoading(emailTemplatesData)}
                            loadingMinHeight={"834px"}
                            loadingMinWidth={"215px"}
                        >
                            {emailTemplateGroups?.map((emailTemplateGroup) => {
                                const templates = emailTemplateGroup?.templates;
                                return (
                                    <div key={emailTemplateGroup.name}>
                                        <div className={styles["template-group"]}>{emailTemplateGroup?.name}</div>
                                        {templates?.map((template) => {
                                            const isTemplateSelected = template?.type === selectedTemplateType;
                                            const containsChanges = getSelectedBrandEnabledLanguages(appProps).find((lc) => hasTemplateChanges(getTemplateId(template?.type, lc)));
                                            return (
                                                <div style={{ width: "100%" }} key={template?.type}>
                                                    <button
                                                        className={classnames(
                                                            styles["template-btn"],
                                                            isTemplateSelected && styles["template-btn-selected"],
                                                            anythingIsLoading && classnames(styles["template-btn-disabled"]),
                                                        )}
                                                        style={{
                                                            width: "100%",
                                                        }}
                                                        disabled={anythingIsLoading || isTemplateSelected}
                                                        onClick={() => {
                                                            onTemplateMenuItemClick(template);
                                                        }}
                                                    >
                                                        {template?.name}
                                                        {containsChanges && (
                                                            <div className={styles["changes-ring-wrapper"]}>
                                                                <div className={styles["changes-ring"]} />
                                                            </div>
                                                        )}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </TurnrCard>
                    </TurnrGrid>
                    {!selectedTemplateType && serviceIsSuccessfullyFetched(emailTemplatesData) && (
                        <TurnrGrid item>
                            <TurnrActionAlert fullWidth>Begin by selecting an email template in the menu on your left</TurnrActionAlert>
                        </TurnrGrid>
                    )}
                    {selectedTemplateType && (
                        <TurnrGrid item>
                            <TurnrCard>
                                <TurnrCardHeader title={selectedTemplateMetaData?.name} subTitle={selectedTemplateMetaData?.description} />
                                <TurnrCardContent>
                                    <TurnrGrid container spacing={2}>
                                        {selectedTemplateType && (
                                            <>
                                                <TurnrGrid container item spacing={2}>
                                                    <TurnrGrid item>
                                                        <LanguageSelect
                                                            style={{
                                                                minWidth: "280px",
                                                            }}
                                                            size={"small"}
                                                            disabled={anythingIsLoading}
                                                            tooltipTitle={""}
                                                            label={"Language"}
                                                            appProps={appProps}
                                                            tooltip={
                                                                <div>
                                                                    Each language can have it's own template. The language that will be used is the language the customer selected when making the
                                                                    Return. Or it will default to your default language, normally English.
                                                                    <br />
                                                                    <br /> If you want more languages then you need to activate them in the App Design page.
                                                                </div>
                                                            }
                                                            value={selectedLanguage}
                                                            onChange={(value) => {
                                                                saveChangesLocally();
                                                                setSelectedLanguage(value);
                                                            }}
                                                        />
                                                    </TurnrGrid>
                                                    <TurnrGrid item>
                                                        <TurnrTooltip
                                                            content={
                                                                <div
                                                                    style={{
                                                                        display: "flex",
                                                                    }}
                                                                >
                                                                    Translates all text in the preview below, no matter the language, into{" "}
                                                                    <LanguageFlag
                                                                        flagStyle={{
                                                                            marginLeft: "10px",
                                                                        }}
                                                                        languageCode={selectedLanguage}
                                                                    />
                                                                </div>
                                                            }
                                                        >
                                                            <TurnrButton
                                                                variant={"outlined"}
                                                                style={{
                                                                    minWidth: "280px",
                                                                }}
                                                                disabled={anythingIsLoading}
                                                                icon={faWandMagicSparkles}
                                                                onClick={translateClick}
                                                            >
                                                                <div
                                                                    style={{
                                                                        display: "flex",
                                                                    }}
                                                                >
                                                                    Auto translate to{" "}
                                                                    <LanguageFlag
                                                                        flagStyle={{
                                                                            marginLeft: "10px",
                                                                        }}
                                                                        languageCode={selectedLanguage}
                                                                    />
                                                                </div>
                                                            </TurnrButton>
                                                        </TurnrTooltip>
                                                    </TurnrGrid>
                                                    <TurnrGrid item>
                                                        <TurnrGrid item>
                                                            <TurnrDropdownSelect
                                                                disabled={anythingIsLoading}
                                                                style={{
                                                                    minWidth: "280px",
                                                                }}
                                                                renderInput={() => (
                                                                    <TurnrTooltip
                                                                        style={{
                                                                            width: "100%",
                                                                        }}
                                                                        content={
                                                                            "If you made big changes to some email template it might be smoother to clone from that template instead of rewriting this one from scratch"
                                                                        }
                                                                    >
                                                                        <TurnrButton disabled={anythingIsLoading} fullWidth variant={"outlined"}>
                                                                            Clone from template
                                                                        </TurnrButton>
                                                                    </TurnrTooltip>
                                                                )}
                                                                value=""
                                                                onChange={(value) => {
                                                                    cloneFromTemplateId(cloneFromTemplateType, value);
                                                                }}
                                                                label={"Clone from"}
                                                                onClose={() => setCloneFromTemplateType(null)}
                                                                renderValue={() => ""}
                                                                renderOption={cloneFromTemplateType && ((languageCode) => <LanguageDropdownRender languageCode={languageCode} appProps={appProps} />)}
                                                                options={
                                                                    cloneFromTemplateType
                                                                        ? getSelectedBrandEnabledLanguages(appProps)
                                                                        : dropdownOptions.map((o) => ({
                                                                              ...o,
                                                                              onClick: () => setCloneFromTemplateType(o.id),
                                                                          }))
                                                                }
                                                            />
                                                        </TurnrGrid>
                                                    </TurnrGrid>
                                                </TurnrGrid>
                                                <VariableChips label={"Required variables"} variables={requiredVariables.concat(optionalVariables)} />
                                                <TurnrGrid item xs={12}>
                                                    <div
                                                        className={"muted subtitle"}
                                                        style={{
                                                            fontSize: "12px",
                                                        }}
                                                    >
                                                        You can write these variables manually as <b>{`{{variableName}}`}</b> or click <b>Merge Tags</b> while editing text
                                                    </div>
                                                </TurnrGrid>
                                                <TurnrGrid container item xs={12} spacing={2}>
                                                    <TurnrGrid item xs={12}>
                                                        <TurnrInput
                                                            value={subject}
                                                            onChange={setSubject}
                                                            disabled={anythingIsLoading}
                                                            key={selectedTemplateId}
                                                            originalValue={originalSubject}
                                                            label={"Subject"}
                                                            style={{
                                                                minWidth: "300px",
                                                            }}
                                                            tooltip={"This is the email title"}
                                                            required={true}
                                                        />
                                                    </TurnrGrid>
                                                    <TurnrGrid item xs={12}>
                                                        <TurnrInput
                                                            value={preHeader}
                                                            onChange={setPreHeader}
                                                            disabled={anythingIsLoading}
                                                            key={selectedTemplateId}
                                                            originalValue={originalPreHeader}
                                                            label={"Preheader"}
                                                            rows={2}
                                                            style={{
                                                                minWidth: "300px",
                                                            }}
                                                            tooltip={"A preheader is the short summary text that follows the subject line when viewing an email from the inbox. This is optional."}
                                                        />
                                                    </TurnrGrid>
                                                </TurnrGrid>
                                            </>
                                        )}
                                        <TurnrGrid item xs={12}>
                                            <TurnrCard>
                                                <div
                                                    style={{
                                                        position: "relative",
                                                    }}
                                                    className={styles["editor-wrapper"]}
                                                >
                                                    <EmailEditor ref={emailEditorRef} onReady={onReady} minHeight={"600px"} options={editorFeatureSettings} />
                                                    <div
                                                        style={{
                                                            display: "none",
                                                        }}
                                                    >
                                                        {/* Just used for loading a blank template that we can easily use for loading empty templates */}
                                                        <EmailEditor ref={hiddenEmailEditorRef} onReady={onHiddenReady} onLoad={onHiddenLoad} minHeight={"600px"} options={editorFeatureSettings} />
                                                    </div>
                                                    {updateIsLoading && (
                                                        <div
                                                            style={{
                                                                position: "absolute",
                                                                top: 0,
                                                                left: 0,
                                                                width: "100%",
                                                                height: "100%",
                                                                opacity: "50%",
                                                            }}
                                                        >
                                                            <SkeletonLoaderCardSlim />
                                                        </div>
                                                    )}
                                                </div>
                                            </TurnrCard>
                                        </TurnrGrid>
                                        <TurnrGrid container spacing={2} item xs={12} justifyContent={"flex-end"}>
                                            <TurnrGrid item>
                                                <TurnrButton
                                                    variant={"outlined"}
                                                    style={{
                                                        marginLeft: "auto",
                                                    }}
                                                    disabled={!firstTemplateIsReady || updateIsLoading}
                                                    loading={updateIsLoading}
                                                    onClick={resetLocalChanges}
                                                >
                                                    Reset
                                                </TurnrButton>
                                            </TurnrGrid>
                                            <TurnrGrid item>
                                                <TurnrButton
                                                    style={{
                                                        marginLeft: "auto",
                                                    }}
                                                    disabled={!firstTemplateIsReady}
                                                    loading={updateIsLoading}
                                                    onClick={onSaveClick}
                                                >
                                                    Save Changes
                                                </TurnrButton>
                                            </TurnrGrid>
                                        </TurnrGrid>
                                    </TurnrGrid>
                                </TurnrCardContent>
                            </TurnrCard>
                        </TurnrGrid>
                    )}
                </TurnrGrid>
            </BrandNeededWrapper>
        </>
    );
}
